import React, { lazy } from 'react';
import { motion } from 'framer-motion';
import { Star, Quote } from 'lucide-react';
type Testimonial = {
  quote: string;
  name: string;
  role: string;
  avatar: string;
};
const testimonials: Testimonial[] = [{
  quote: 'Antes me tomaba casi 4 horas armar un informe completo. Ahora lo hago en menos de la mitad del tiempo y mis clientas me dicen que se ven mucho más profesionales. ¡No puedo creer que no lo haya encontrado antes!',
  name: 'Luciana Herrera',
  role: 'Astróloga • Buenos Aires, 38 años',
  avatar: 'https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=300&h=300&fit=crop&crop=face'
}, {
  quote: 'Estaba agotada de trabajar tanto y ganar lo mismo. Con el sistema pude subir mis precios porque ahora mis entregas tienen otro nivel. Mis clientas notan la diferencia y me recomiendan más.',
  name: 'Natalia Suárez',
  role: 'Astróloga • Córdoba, 42 años',
  avatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=300&h=300&fit=crop&crop=face'
}, {
  quote: 'Lo que más me gustó es que no perdí mi esencia. Los informes siguen siendo míos, pero ahora están organizados y se ven increíbles. Recuperé tiempo para mi familia y para seguir estudiando.',
  name: 'Julieta Navarro',
  role: 'Astróloga • Mendoza, 35 años',
  avatar: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=300&h=300&fit=crop&crop=face'
}, {
  quote: 'Tenía miedo de que fuera algo muy técnico o difícil de usar. Pero es súper intuitivo. En una semana ya estaba entregando informes con el nuevo formato y mis clientas quedaron encantadas.',
  name: 'Camila Benítez',
  role: 'Astróloga • Rosario, 29 años',
  avatar: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=300&h=300&fit=crop&crop=face'
}, {
  quote: 'Llevo 15 años haciendo cartas y nunca había encontrado algo así. Me ayudó a profesionalizar mi trabajo sin perder la calidez que mis clientas valoran. Ahora puedo atender más consultas sin sentirme abrumada.',
  name: 'Verónica Molina',
  role: 'Astróloga • Mar del Plata, 52 años',
  avatar: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=300&h=300&fit=crop&crop=face'
}];
// Pre-computed star particle positions to avoid hydration mismatches
const stars = Array.from({
  length: 40
}, (_, i) => ({
  id: i,
  top: `${i * 37 % 100}%`,
  left: `${i * 53 % 100}%`,
  size: i % 3 + 1,
  delay: i % 5 * 0.7,
  duration: 3 + i % 4
}));
function TestimonialCard({
  t
}: {
  t: Testimonial;
}) {
  return <motion.div whileHover={{
    scale: 1.04,
    y: -6
  }} transition={{
    type: 'spring',
    stiffness: 300,
    damping: 22
  }} className="relative w-[340px] md:w-[380px] shrink-0 mx-4 select-none" data-id="element-256" data-mp="1ps2b8mpd7u3r">
      {/* Glassmorphism card */}
      <div className="relative h-full rounded-2xl p-7 bg-white/[0.04] backdrop-blur-xl border border-white/10 shadow-[0_20px_60px_-15px_rgba(0,0,0,0.7)] overflow-hidden" data-id="element-257" data-mp="3f7rwqmvevogv">
        {/* Gold corner glow */}
        <div className="absolute -top-12 -right-12 w-32 h-32 bg-primary/20 rounded-full blur-3xl pointer-events-none" data-id="element-258" data-mp="3rnhyjmi5gnot"></div>
        <div className="absolute -bottom-12 -left-12 w-32 h-32 bg-blue-500/10 rounded-full blur-3xl pointer-events-none" data-id="element-259" data-mp="chswmqoie11"></div>

        {/* Decorative quote icon */}
        <Quote className="absolute top-4 right-4 w-8 h-8 text-primary/20" aria-hidden="true" data-id="element-260" data-mp="33sf5f2cbby2j" />

        <div className="relative z-10" data-id="element-261" data-mp="38sazo9v7kb4w">
          {/* Stars */}
          <div className="flex gap-1 mb-5" data-id="element-262" data-mp="3do8eucuqjbg3">
            {[...Array(5)].map((_, j) => <Star key={j} className="w-4 h-4 fill-primary text-primary drop-shadow-[0_0_4px_rgba(212,168,87,0.6)]" data-id="element-263" data-mp="3hr51qfrb5tpu" data-mpi={j} />)}
          </div>

          {/* Quote */}
          <p className="text-foreground/90 text-base leading-relaxed mb-6 italic min-h-[180px]" data-id="element-264" data-mp="wzcu5ay2i0oq">
            "{t.quote}"
          </p>

          {/* Author */}
          <div className="flex items-center gap-3 pt-4 border-t border-white/10" data-id="element-265" data-mp="qkxfjnh7mc4i">
            <div className="relative" data-id="element-266" data-mp="15hoj88xx29zx">
              <div className="absolute inset-0 bg-primary/30 rounded-full blur-md" data-id="element-267" data-mp="15iprk5njsidy"></div>
              <img src={t.avatar} alt={t.name} className="relative w-12 h-12 rounded-full object-cover border-2 border-primary/40 shadow-lg" loading="lazy" data-id="element-268" data-mp="10ibu1hjkoqu6" />
            </div>
            <div data-id="element-269" data-mp="19h6zgreslwwf">
              <div className="font-semibold text-foreground" data-id="element-270" data-mp="1o8645rcic1aq">{t.name}</div>
              <div className="text-xs text-primary/80 uppercase tracking-wider font-medium" data-id="element-271" data-mp="1oa8te95u5qee">
                {t.role}
              </div>
            </div>
          </div>
        </div>
      </div>
    </motion.div>;
}
export function TestimonialsMarquee() {
  // Duplicate for seamless loop
  const loop = [...testimonials, ...testimonials];
  return <section id="testimonios" className="relative py-28 overflow-hidden border-y border-white/5" style={{
    background: 'radial-gradient(ellipse at top, #0d1238 0%, #050518 60%, #020210 100%)'
  }} data-id="element-272" data-mp="2bvb752ni5llz">
      {/* Animated star particles */}
      <div className="absolute inset-0 pointer-events-none" data-id="element-273" data-mp="27tpp72w42h4x">
        {stars.map(s => <motion.div key={s.id} className="absolute rounded-full bg-white" style={{
        top: s.top,
        left: s.left,
        width: s.size,
        height: s.size
      }} animate={{
        opacity: [0.2, 1, 0.2],
        scale: [0.8, 1.4, 0.8]
      }} transition={{
        duration: s.duration,
        delay: s.delay,
        repeat: Infinity,
        ease: 'easeInOut'
      }} data-id="element-274" data-mp="1t5bmcryp2sod" />)}
      </div>

      {/* Decorative cosmic glows */}
      <div className="absolute top-1/2 left-1/4 -translate-y-1/2 w-[500px] h-[500px] bg-primary/5 rounded-full blur-3xl pointer-events-none" data-id="element-275" data-mp="3guznanl23p7v"></div>
      <div className="absolute top-1/2 right-1/4 -translate-y-1/2 w-[500px] h-[500px] bg-blue-500/10 rounded-full blur-3xl pointer-events-none" data-id="element-276" data-mp="3c26ffquak0rk"></div>

      <div className="relative z-10 container mx-auto px-4 mb-16 text-center max-w-3xl" data-id="element-277" data-mp="333l3wfn8lm0a">
        <h2 className="text-3xl md:text-5xl font-heading font-bold mb-5 text-foreground leading-tight" data-id="element-278" data-mp="3no5fjksib5b">
          Lo que dicen las{' '}
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-amber-200" data-id="element-279" data-mp="3vsryj3jklrg9">
            astrólogas profesionales
          </span>{' '}
          que ya transformaron su práctica
        </h2>
        <p className="text-lg text-muted-foreground" data-id="element-280" data-mp="3lt138zn6r8f4">
          Más claridad. Más seguridad. Menos caos.
        </p>
      </div>

      {/* Marquee */}
      <div className="relative z-10 group" data-id="element-281" data-mp="2aenoqlebo5br">
        {/* Edge fade masks */}
        <div className="absolute left-0 top-0 bottom-0 w-24 md:w-40 bg-gradient-to-r from-[#050518] to-transparent z-20 pointer-events-none" data-id="element-282" data-mp="2agqkzn62iuox"></div>
        <div className="absolute right-0 top-0 bottom-0 w-24 md:w-40 bg-gradient-to-l from-[#020210] to-transparent z-20 pointer-events-none" data-id="element-283" data-mp="25alfd5mspq3v"></div>

        <motion.div className="flex py-4" animate={{
        x: ['0%', '-50%']
      }} transition={{
        duration: 50,
        ease: 'linear',
        repeat: Infinity
      }} style={{
        width: 'max-content'
      }} data-id="element-284" data-mp="2a6sb65lh9l83">
          {loop.map((t, i) => <TestimonialCard key={i} t={t} data-id="element-285" data-mp="2t062m0pzf9iy" data-mpi={i} />)}
        </motion.div>
      </div>
    </section>;
}